'use client';

import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { motion } from "motion/react";
import { PhilosophyData } from '@/types/philosophy';

interface VisionMissionProps {
  vision?: PhilosophyData | null;
  mission?: PhilosophyData | null;
}


export const MissionAndVission: React.FC<VisionMissionProps> = ({ vision, mission }) => {
  const [activeTab, setActiveTab] = useState<'vision' | 'mission'>('vision');

  const hasVision = vision && typeof vision.content === "string" && vision.content.trim() !== "";
  const hasMission = mission && typeof mission.content === "string" && mission.content.trim() !== "";

  const hasBoth = hasVision && hasMission;
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Pick the right starting tab if only mission exists
  useEffect(() => {
    if (!hasVision && hasMission) {
      setActiveTab('mission');
    }
  }, [hasVision, hasMission]);

  // Auto toggle only if both exist AND not hovered AND not expanded
  useEffect(() => {
    if (!hasBoth || isHovered || isOpen) return;

    const interval = setInterval(() => {
      setActiveTab((prevTab) => (prevTab === 'vision' ? 'mission' : 'vision'));
    }, 5000);

    return () => clearInterval(interval);
  }, [hasBoth, isHovered, isOpen]);

  // Decide which content to show
  let content: PhilosophyData | null = null;
  if (hasBoth) {
    content = activeTab === 'vision' ? vision! : mission!;
  } else if (hasVision) {
    content = vision!;
  } else if (hasMission) {
    content = mission!;
  }

  if (!content) return null; // nothing to show

  return (
    <section className="">
      <div className="w-full rounded-2xl flex flex-col md:flex-row gap-4 md:gap-6 ">
        {/* Buttons */}
        <div className="flex items-center justify-center">
          <div className="rotate-none md:-rotate-90">
            <div className="flex flex-col gap-4">
              {hasVision && (
                <Button
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onClick={() => setActiveTab('vision')}
                  className={`font-medium rounded-full px-12 py-1 cursor-pointer ${
                    activeTab === 'vision' || (!hasBoth && hasVision)
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-500 hover:text-white'
                  }`}
                >
                  Our Vision
                </Button>
              )}
              {hasMission && (
                <Button
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onClick={() => setActiveTab('mission')}
                  className={`font-medium rounded-full px-4 md:px-12 py-1 cursor-pointer ${
                    activeTab === 'mission' || (!hasBoth && hasMission)
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-500 hover:text-white'
                  }`}
                >
                  Our Mission
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="w-full">
          <motion.div
            className={isOpen ? "h-auto" : " h-50 sm:h-40 md:h-33 lg:h-33 overflow-hidden"} 
            key={activeTab} 
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
              exit: { opacity: 0 },
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 1 }}
          >
            <h2 className="text-lg md:text-xl font-bold text-foreground mb-4">
              {typeof content.title === "string" ? content.title : ""}
            </h2>

            <div
              className="text-sm text-muted-foreground w-full leading-relaxed overflow-hidden"
              dangerouslySetInnerHTML={{
                __html: typeof content.content === "string" ? content.content : ""
              }}
            />
          </motion.div>
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="float-right font-medium py-1 text-primary text-sm  flex items-center cursor-pointer ">
              {isOpen ? "Read Less" : "Read More"} 
              <ChevronDown className={` w-4 h-4 ml-1 transition-all duration-200 ${isOpen ? "rotate-180" : ""}`}  />
          </button>
        </div>
      </div>
    </section>
  );
};
